import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  formatPrice,
  getPrimaryImage,
  getPropertyAddress,
  getPropertyAltText,
  getPropertyArea,
  getPropertyState,
} from '../propertyUtils';
import { useCurrency } from '../context/CurrencyContext';
import { propertyHasRooms } from '../propertyConfig';
import { TagIcon, MapPinIcon } from './IconSet';

const ROTATE_INTERVAL_MS = 2 * 60 * 1000;
const GROUP_SIZE = 2;

function SpotlightCard({ listing, placeholderImage, onOpenProperty }) {
  const currencyCtx = useCurrency();
  const listingType = listing.listing_type || listing.type || 'rent';
  const isShortStay = listingType === 'short-let' || listingType === 'hotel';
  const propertyType = listing.propertyType || listing.property_type || 'Apartment';

  return (
    <article
      className="spotlight-strip spotlight-strip--clickable"
      onClick={() => onOpenProperty(listing.id)}
    >
      <div className="spotlight-strip__media">
        <img
          src={getPrimaryImage(listing, placeholderImage)}
          alt={getPropertyAltText(listing)}
          loading="lazy"
        />
        <span className="spotlight-strip__badge">
          <TagIcon size={12} /> Featured
        </span>
      </div>
      <div className="spotlight-strip__content">
        <div className="spotlight-strip__body">
          <p className="spotlight-strip__kicker">
            <MapPinIcon size={12} />
            {getPropertyAddress(listing) ||
              `${getPropertyArea(listing) || 'Nigeria'}, ${getPropertyState(listing) || ''}`}
          </p>
          <p className="spotlight-strip__title">{listing.title}</p>
          <p className="spotlight-strip__facts">
            {listingType} • {propertyType}
            {propertyHasRooms(propertyType) && ` • Beds ${listing.bedrooms || 'N/A'}`}
          </p>
          <p className="spotlight-strip__price">
            {isShortStay && listing.price_per_night ? (
              <>{formatPrice(listing.price_per_night, currencyCtx)} / night</>
            ) : (
              <>
                {formatPrice(listing.price || 0, currencyCtx)}
                {listingType === 'rent' ? ' / year' : ''}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="spotlight-strip__cta"
          onClick={() => onOpenProperty(listing.id)}
        >
          View property
        </button>
      </div>
    </article>
  );
}

function PropertySpotlightBanner({ properties, placeholderImage, onOpenProperty }) {
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = Math.ceil(properties.length / GROUP_SIZE);

  useEffect(() => {
    const count = Math.ceil(properties.length / GROUP_SIZE);
    setPageIndex(count > 0 ? Math.floor(Math.random() * count) : 0);
  }, [properties]);

  useEffect(() => {
    if (pageCount <= 1) {
      return undefined;
    }
    const timer = setInterval(() => {
      setPageIndex((current) => (current + 1) % pageCount);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pageCount]);

  if (properties.length === 0) {
    return null;
  }

  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const start = safePageIndex * GROUP_SIZE;
  const visible = properties.slice(start, start + GROUP_SIZE);

  return (
    <div className="spotlight-strip-row">
      <div className="spotlight-strip-row__cards" key={visible.map((item) => item.id).join('-')}>
        {visible.map((listing) => (
          <SpotlightCard
            key={listing.id}
            listing={listing}
            placeholderImage={placeholderImage}
            onOpenProperty={onOpenProperty}
          />
        ))}
      </div>
      {pageCount > 1 && (
        <div className="spotlight-strip-row__dots">
          {Array.from({ length: pageCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              className={index === safePageIndex ? 'active' : ''}
              aria-label={`Show spotlight group ${index + 1} of ${pageCount}`}
              aria-current={index === safePageIndex}
              onClick={() => setPageIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

PropertySpotlightBanner.propTypes = {
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
    })
  ).isRequired,
  placeholderImage: PropTypes.string,
  onOpenProperty: PropTypes.func.isRequired,
};

PropertySpotlightBanner.defaultProps = {
  placeholderImage: '',
};

export default PropertySpotlightBanner;
